import { Component, input, computed } from '@angular/core';
import { CURRENCY_DATA, CurrencyCode } from '@gs-2.0/core';

/**
 * A simple currency indicator component that displays a country flag and currency code.
 * Based on Figma design.
 * Figma URL: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining---Final-Pages?node-id=90-14246
 *
 * @example
 * ```html
 * <!-- White text variant (default) for dark backgrounds -->
 * <app-currency-indicator
 *   countryCode="US"
 *   currencyCode="USD">
 * </app-currency-indicator>
 *
 * <!-- Black text variant for light backgrounds -->
 * <app-currency-indicator
 *   countryCode="US"
 *   currencyCode="USD"
 *   variant="black">
 * </app-currency-indicator>
 *
 * <!-- Auto-derive country from currency -->
 * <app-currency-indicator
 *   currencyCode="USD">
 * </app-currency-indicator>
 *
 * <!-- Flag only (no text) -->
 * <app-currency-indicator
 *   currencyCode="USD"
 *   [showText]="false">
 * </app-currency-indicator>
 * ```
 */
@Component({
  selector: 'app-currency-indicator',
  templateUrl: './currency-indicator.component.html',
  styleUrls: ['./currency-indicator.component.scss'],
  standalone: false,
})
export class CurrencyIndicatorComponent {
  /**
   * ISO 3166-1 alpha-2 country code (e.g., 'US', 'GB', 'FR')
   * Optional - if not provided, will be derived from currencyCode
   */
  countryCode = input<string>();

  /**
   * ISO 4217 currency code (e.g., 'USD', 'GBP', 'EUR')
   * The currency code to display next to the flag
   */
  currencyCode = input.required<string>();

  /**
   * Visual variant of the currency indicator
   * - 'white': White text color (default)
   * - 'black': Black text color
   */
  variant = input<'white' | 'black'>('white');

  /**
   * Whether to show the currency code text next to the flag
   * - true: Show both flag and text (default)
   * - false: Show only the flag
   */
  showText = input<boolean>(true);

  /**
   * Gets the country code - either from input or derived from currency
   */
  protected effectiveCountryCode = computed(() => {
    const explicitCode = this.countryCode();
    if (explicitCode) {
      return explicitCode;
    }

    // Derive from currency code
    const currency = this.currencyCode().toUpperCase() as CurrencyCode;
    return CURRENCY_DATA[currency]?.countryCode || 'US'; // Fallback to US if not found
  });

  /**
   * Gets the CSS class for the flag based on country code
   * Uses country-flag-icons package format: flag:XX
   */
  protected flagClass = computed(() => {
    const code = this.effectiveCountryCode().toUpperCase();
    return `flag:${code}`;
  });
}
